import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Check, HelpCircle, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { PRICING_TIERS, CurrencyType, getUserCurrency, saveCurrencyPreference, getCurrencySymbol } from "@/utils/currency";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CoffeeComparisonBlock } from "@/components/landing/CoffeeComparisonBlock";
import { SEOHead } from "@/components/seo/SEOHead";

export default function Pricing() {
  const [currency, setCurrency] = useState<CurrencyType>("EUR");
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    setCurrency(getUserCurrency());
  }, []);

  const handleCurrencyToggle = () => {
    const newCurrency = currency === "EUR" ? "USD" : "EUR";
    setCurrency(newCurrency);
    saveCurrencyPreference(newCurrency);
  };

  const handlePlanSelection = async () => {
    if (!user) {
      navigate("/auth/register");
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("No authentication session");
      }

      const response = await fetch(`https://ngfwjlgmdcoxewihfmlt.supabase.co/functions/v1/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          planId: "pro",
          billingPeriod: "monthly",
          currency: currency.toLowerCase(),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to create checkout session");
      }

      window.location.href = data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Checkout failed",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const proTier = PRICING_TIERS.find((t) => t.id === "pro");
  const proPriceLabel = proTier ? `${getCurrencySymbol(currency)}${proTier.prices[currency]}` : "€29";

  const renderProCard = () => {
    if (!proTier) return null;
    const tier = proTier;
    return (
      <motion.div
        variants={itemVariants}
        className="relative rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-md hover:shadow-lg transition-all"
      >
        <div className="p-6 md:p-8">
          <h3 className="text-2xl md:text-3xl font-bold text-foreground">
            {tier.name}
          </h3>
          <p className="mt-2 min-h-[48px] text-zinc-600 dark:text-zinc-400">
            {tier.description}
          </p>
          <div className="mt-4 mb-6">
            <div className="flex items-baseline">
              <span className="text-4xl font-bold text-foreground">
                {getCurrencySymbol(currency)}{tier.prices[currency]}
              </span>
              <span className="ml-2 text-base text-zinc-500 dark:text-zinc-400">
                /month
              </span>
            </div>
          </div>
          <Button
            onClick={handlePlanSelection}
            size="lg"
            className="w-full py-6 text-base font-bold bg-primary hover:bg-primary/90 text-primary-foreground transition-colors"
          >
            {user ? "Subscribe" : "Start subscription"}
          </Button>
          <div className="mt-6 md:mt-8 space-y-3">
            {tier.features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3 rounded-xl">
                <div className="h-6 w-6 rounded-full flex items-center justify-center shrink-0 bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300">
                  <Check className="h-4 w-4" />
                </div>
                <div className="flex-1 text-sm font-medium leading-relaxed text-zinc-700 dark:text-zinc-300">
                  {feature.text}
                  {"tooltip" in feature && feature.tooltip ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="ml-2 inline-flex items-center justify-center align-middle rounded-full text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300">
                          <HelpCircle className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-sm">
                        {feature.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white dark:from-zinc-950 dark:to-zinc-900">
      <SEOHead
        title="Pricing — Supercharge your product launch | LaunchGuide.io"
        description="One simple plan unlocks playbooks, AI marketing agents, and launch tools. See LaunchGuide pricing and what is included."
        pathname="/pricing"
      />
      <Navbar />
      <div className="container mx-auto px-4 py-16 md:py-24 max-w-7xl">
        {/* Hero — 8px rhythm, clear hierarchy */}
        <motion.div
          className="text-center mb-16 md:mb-20 space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Badge
            variant="outline"
            className="px-4 py-1.5 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium"
          >
            Your All-in-one Launch Stack
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-sky-500 dark:from-blue-400 dark:to-sky-400 tracking-tight leading-tight px-4">
            Supercharge your product launch
          </h1>
          <p className="text-lg md:text-xl text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto font-medium">
            One simple plan: {proPriceLabel}/month unlocks everything.
          </p>
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={handleCurrencyToggle}
              className="inline-flex items-center gap-1 rounded-full border border-zinc-200 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 px-4 py-2 text-xs font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            >
              {currency === "EUR" ? "€ EUR" : "$ USD"}
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </div>
        </motion.div>

        {/* Pro + Coffee comparison side by side on lg; stacked on small */}
        <TooltipProvider>
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-[1fr,minmax(0,360px)] gap-6 lg:gap-8 max-w-5xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {renderProCard()}
            <motion.div variants={itemVariants} className="min-h-[280px] lg:min-h-0 flex flex-col">
              <CoffeeComparisonBlock
                priceLabel={proPriceLabel}
                className="h-full"
                compact={false}
              />
            </motion.div>
          </motion.div>
        </TooltipProvider>

        <motion.div
          className="flex justify-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="flex items-center space-x-2 text-zinc-600 dark:text-zinc-400 bg-white dark:bg-zinc-800 px-6 py-3 rounded-full shadow-sm border border-zinc-200 dark:border-zinc-700">
            <Users className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium">Trusted by 100+ SaaS builders</span>
          </div>
        </motion.div>
        
        <motion.div
          className="mt-24 max-w-3xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 md:mb-12 bg-clip-text text-transparent bg-gradient-to-r from-zinc-900 to-zinc-700 dark:from-white dark:to-zinc-300">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4 max-w-3xl mx-auto">
            <div className="bg-white dark:bg-zinc-900 rounded-xl p-5 md:p-6 border border-zinc-200 dark:border-zinc-800 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">
                What’s included in Pro?
              </h3>
              <p className="text-zinc-600 dark:text-zinc-300">
                Full access to all growth playbooks and phases, AI tools (website analysis, research, competitor insights, content topics), the Marketing Vault, Product Hunt and Reddit launch guides, directories, and all future updates. One plan, everything included.
              </p>
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl p-5 md:p-6 border border-zinc-200 dark:border-zinc-800 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">
                How does billing work?
              </h3>
              <p className="text-zinc-600 dark:text-zinc-300">
                You’re charged monthly in your chosen currency (EUR or USD). Payment is processed securely by Stripe. Your next billing date is shown in your account settings after you subscribe.
              </p>
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl p-5 md:p-6 border border-zinc-200 dark:border-zinc-800 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">
                Can I cancel anytime?
              </h3>
              <p className="text-zinc-600 dark:text-zinc-300">
                Yes. Cancel from your billing or account settings at any time. You keep access until the end of your current billing period; we don’t charge you again after cancellation.
              </p>
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl p-5 md:p-6 border border-zinc-200 dark:border-zinc-800 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">
                Do you offer refunds?
              </h3>
              <p className="text-zinc-600 dark:text-zinc-300">
                We don’t offer refunds for partial months. If something’s wrong or you’re not satisfied, contact us and we’ll work with you to make it right.
              </p>
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl p-5 md:p-6 border border-zinc-200 dark:border-zinc-800 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">
                Is my payment secure?
              </h3>
              <p className="text-zinc-600 dark:text-zinc-300">
                Yes. We use Stripe for all payments. Your card details are never stored on our servers. Stripe is PCI-DSS compliant and used by millions of businesses worldwide.
              </p>
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl p-5 md:p-6 border border-zinc-200 dark:border-zinc-800 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">
                Who is LaunchGuide for?
              </h3>
              <p className="text-zinc-600 dark:text-zinc-300">
                Founders, indie hackers, and small product teams who want a clear launch and growth system without hiring a marketing team. If you’re building a product and want playbooks, AI-powered research, and content tools in one place, LaunchGuide is for you.
              </p>
            </div>

          </div>
        </motion.div>
        
        <motion.div
          className="mt-24 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-sky-500 dark:from-blue-700 dark:to-blue-700 rounded-3xl p-12 md:p-16 relative overflow-hidden shadow-xl">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{ 
                backgroundImage: "radial-gradient(circle, rgba(255, 255, 255, 0.8) 1px, transparent 1px)",
                backgroundSize: "30px 30px",
              }}></div>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">Ready to launch with Pro?</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-10">
                Subscribe for {proPriceLabel}/month to unlock every playbook, AI tool, and analysis workflow.
              </p>
              
              <Button
                size="lg"
                className="bg-white text-primary font-bold text-base py-6 px-8 rounded-xl shadow-lg hover:bg-white/90 transition"
                onClick={handlePlanSelection}
              >
                Subscribe for {proPriceLabel}/month
              </Button>
              
              <p className="text-sm text-blue-100/80 mt-8">
                Cancel anytime. Secure Stripe checkout.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
}
